<script setup lang="ts">
/**
 * UiKitInput — текстовый инпут HeroUI UI-kit. Figma: 2934-167912.
 * Цвета только из токенов (классы hui-*).
 */
import { FOCUS_BORDER, type HuiColor, type HuiSize } from './palette'

type InputVariant = 'flat' | 'bordered' | 'faded' | 'underlined'
type InputRadius = 'none' | 'sm' | 'md' | 'lg' | 'full'

const props = withDefaults(
  defineProps<{
    color?: HuiColor
    variant?: InputVariant
    size?: HuiSize
    radius?: InputRadius
    type?: string
    label?: string
    placeholder?: string
    description?: string
    errorMessage?: string
    suffix?: string
    disabled?: boolean
    invalid?: boolean
    readonly?: boolean
    clearable?: boolean
  }>(),
  {
    color: 'primary',
    variant: 'flat',
    size: 'md',
    radius: 'md',
    type: 'text',
    label: '',
    placeholder: '',
    description: '',
    errorMessage: '',
    suffix: '',
    disabled: false,
    invalid: false,
    readonly: false,
    clearable: false,
  },
)

const emit = defineEmits<{ (e: 'blur', ev: FocusEvent): void }>()

const model = defineModel<string | number | null>({ default: '' })

defineOptions({ name: 'UiKitInput' })

// Поля с паролем/токеном маскируются, но получают кнопку-«глазик» для показа значения.
const isPassword = computed(() => props.type === 'password')
const showPassword = ref(false)
const resolvedType = computed(() => (isPassword.value && showPassword.value ? 'text' : props.type))

// Для type="number" приводим к Number (компонентный v-model не кастует .number автоматически)
function onInput(e: Event) {
  const raw = (e.target as HTMLInputElement).value
  model.value = props.type === 'number' ? (raw === '' ? null : Number(raw)) : raw
}

const SIZE: Record<HuiSize, string> = { sm: 'h-9 text-sm px-3', md: 'h-11 text-base px-3.5', lg: 'h-12 text-base px-4' }
const RADIUS: Record<InputRadius, string> = {
  none: 'rounded-none', sm: 'rounded-hui-sm', md: 'rounded-hui-md', lg: 'rounded-hui-lg', full: 'rounded-full',
}

const wrapperClass = computed(() => {
  const c = props.color
  const inv = props.invalid
  const base = 'group flex items-center gap-2 w-full box-border overflow-hidden transition-colors duration-150'
  const radius = props.variant === 'underlined' ? 'rounded-none' : RADIUS[props.radius]
  const idleBordered = inv ? 'border-hui-danger' : `border-hui-default-200 hover:border-hui-default-400 ${FOCUS_BORDER[c]}`
  const idleUnderlined = inv ? 'border-hui-danger' : `border-hui-default-300 hover:border-hui-default-400 ${FOCUS_BORDER[c]}`
  let variant: string
  switch (props.variant) {
    case 'flat': {
      variant = inv
        ? 'bg-hui-danger/10 border-2 border-transparent'
        : 'bg-hui-default-100 border-2 border-transparent hover:bg-hui-default-200 focus-within:bg-hui-default-200'
      break
    }
    case 'bordered': {
      variant = `bg-transparent border-2 ${idleBordered}`
      break
    }
    case 'faded': {
      variant = `bg-hui-default-100 border-2 ${idleBordered}`
      break
    }
    case 'underlined': {
      variant = `bg-transparent border-b-2 px-1 ${idleUnderlined}`
      break
    }
    default: {
      variant = ''
    }
  }
  return [base, SIZE[props.size], radius, variant, props.disabled ? 'opacity-50 pointer-events-none' : '']
})

const labelClass = computed(() => [props.size === 'sm' ? 'text-xs' : 'text-sm', props.invalid ? 'text-hui-danger' : 'text-hui-foreground/70'])

function clear() {
  model.value = ''
}
</script>

<template>
  <div class="flex flex-col gap-1.5 w-full">
    <label v-if="label" class="font-medium leading-none" :class="labelClass">{{ label }}</label>

    <div :class="wrapperClass">
      <span v-if="$slots.leading" class="shrink-0 flex items-center text-hui-foreground/50"><slot name="leading" /></span>

      <input
        :value="model"
        :type="resolvedType"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        class="flex-1 min-w-0 bg-transparent outline-none text-hui-foreground placeholder:text-hui-foreground/40 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        @input="onInput"
        @blur="emit('blur', $event)"
      >

      <span v-if="suffix" class="shrink-0 text-sm text-hui-foreground/50 select-none">{{ suffix }}</span>

      <button
        v-if="isPassword && !disabled"
        type="button"
        class="shrink-0 inline-flex items-center justify-center w-5 h-5 text-hui-foreground/50 hover:text-hui-foreground transition-colors outline-none"
        :aria-label="showPassword ? 'Скрыть' : 'Показать'"
        :title="showPassword ? 'Скрыть' : 'Показать'"
        @click="showPassword = !showPassword"
      >
        <svg v-if="showPassword" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 11.544 11.544M14.12 14.12 9.88 9.88" />
        </svg>
        <svg v-else class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
        </svg>
      </button>

      <button
        v-if="clearable && model !== '' && model != null && !disabled && !readonly"
        type="button"
        class="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-hui-foreground/50 hover:text-hui-foreground transition-colors outline-none"
        aria-label="Очистить"
        @click="clear"
      >
        <svg class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M6 6l8 8M14 6l-8 8" stroke-linecap="round" />
        </svg>
      </button>

      <span v-if="$slots.trailing" class="shrink-0 flex items-center text-hui-foreground/50"><slot name="trailing" /></span>
    </div>

    <p v-if="invalid && errorMessage" class="text-xs text-hui-danger">{{ errorMessage }}</p>
    <p v-else-if="description" class="text-xs text-hui-foreground/60">{{ description }}</p>
  </div>
</template>

<style scoped>
/*
 * Браузерное автозаполнение (Chrome/Edge/Safari/Opera) красит поле своим фоном
 * поверх hui-темы. Маскируем его inset-тенью под цвет инпута и держим текст в
 * цвете темы; длинная transition — бэкап против мигания фона при автозаполнении.
 */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-text-fill-color: rgb(var(--hui-foreground));
  caret-color: rgb(var(--hui-foreground));
  -webkit-box-shadow: 0 0 0 1000px rgb(var(--hui-default-100)) inset;
  box-shadow: 0 0 0 1000px rgb(var(--hui-default-100)) inset;
  border-radius: inherit;
  transition: background-color 600000s 0s, color 600000s 0s;
}
</style>
